import type {
  ExecutiveInsights,
  ExecutiveInsightItem,
  HealthMetrics,
  PendingApproval,
} from '../services/dashboard'

export interface DashboardSnapshot {
  stats: {
    totalProjects: number
    totalBudget: number
    atRiskProjects: number
    portfolioHealth: number
  }
  projectStatus: { name: string; value: number; color?: string }[]
  aiInsights: ExecutiveInsights
  pendingApprovals: PendingApproval[]
  healthMetrics: HealthMetrics | null
  topRisks: Array<Record<string, unknown>>
  criticalProjects: Array<Record<string, unknown>>
}

function timestamp() {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

function normalizeInsights(items: ExecutiveInsightItem[] | string[] | undefined): ExecutiveInsightItem[] {
  if (!items) return []
  return items.map((raw) => (typeof raw === 'string' ? { summary: raw } : raw))
}

// ─── PPTX export ──────────────────────────────────────────────────────────────

const BRAND = {
  primary: '102A43',
  primaryLight: '486581',
  accent: 'DAA520',
  bg: 'F0F4F8',
  text: '102A43',
  muted: '486581',
  good: '10B981',
  warn: 'F59E0B',
  bad: 'EF4444',
  white: 'FFFFFF',
}

export async function exportDashboardPPTX(snapshot: DashboardSnapshot) {
  const PptxGenJS = (await import('pptxgenjs')).default
  const pres = new PptxGenJS()
  pres.layout = 'LAYOUT_WIDE' // 13.333 x 7.5 in
  pres.title = 'Executive Command Center Briefing'
  pres.company = 'Karyva'

  const now = new Date()
  const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

  const { stats, projectStatus, aiInsights, pendingApprovals, healthMetrics, topRisks, criticalProjects } = snapshot

  // ── Slide 1: Title ─────────────────────────────────────────────────────────
  {
    const s = pres.addSlide()
    s.background = { color: BRAND.primary }
    s.addText('Executive Command Center', {
      x: 0.6, y: 2.6, w: 12, h: 0.9,
      fontSize: 40, bold: true, color: BRAND.white, fontFace: 'Calibri',
    })
    s.addText('Portfolio Briefing', {
      x: 0.6, y: 3.5, w: 12, h: 0.6,
      fontSize: 24, color: BRAND.accent, fontFace: 'Calibri',
    })
    s.addText(dateStr, {
      x: 0.6, y: 4.3, w: 12, h: 0.4,
      fontSize: 16, color: 'BCCCDC', fontFace: 'Calibri',
    })
    s.addShape(pres.ShapeType.rect, {
      x: 0.6, y: 6.6, w: 1.5, h: 0.06, fill: { color: BRAND.accent }, line: { color: BRAND.accent },
    })
  }

  // helper: section header band
  const addHeader = (slide: PptxGenJS.Slide, title: string) => {
    slide.background = { color: BRAND.white }
    slide.addShape(pres.ShapeType.rect, {
      x: 0, y: 0, w: 13.333, h: 0.7, fill: { color: BRAND.primary }, line: { color: BRAND.primary },
    })
    slide.addText(title, {
      x: 0.5, y: 0.1, w: 12.3, h: 0.5,
      fontSize: 22, bold: true, color: BRAND.white, fontFace: 'Calibri',
    })
    slide.addText(dateStr, {
      x: 0.5, y: 7.05, w: 12.3, h: 0.3,
      fontSize: 10, color: BRAND.muted, fontFace: 'Calibri', align: 'right',
    })
  }

  // ── Slide 2: KPIs ──────────────────────────────────────────────────────────
  {
    const s = pres.addSlide()
    addHeader(s, 'Key Metrics')

    const cards = [
      { label: 'Active Projects', value: String(stats.totalProjects), accent: BRAND.primaryLight },
      { label: 'Total Budget', value: formatMoney(stats.totalBudget), accent: BRAND.good },
      { label: 'At-Risk Projects', value: String(stats.atRiskProjects), accent: BRAND.bad },
      { label: 'Portfolio Health', value: `${stats.portfolioHealth}%`, accent: BRAND.accent },
    ]

    const cardW = 2.9
    const cardH = 2.0
    const gap = 0.25
    const totalW = cards.length * cardW + (cards.length - 1) * gap
    const startX = (13.333 - totalW) / 2
    const cardY = 1.6

    cards.forEach((c, i) => {
      const x = startX + i * (cardW + gap)
      s.addShape(pres.ShapeType.roundRect, {
        x, y: cardY, w: cardW, h: cardH,
        fill: { color: BRAND.bg }, line: { color: 'BCCCDC', width: 1 },
        rectRadius: 0.1,
      })
      s.addShape(pres.ShapeType.rect, {
        x, y: cardY, w: cardW, h: 0.08, fill: { color: c.accent }, line: { color: c.accent },
      })
      s.addText(c.label.toUpperCase(), {
        x: x + 0.2, y: cardY + 0.25, w: cardW - 0.4, h: 0.4,
        fontSize: 11, color: BRAND.muted, bold: true, fontFace: 'Calibri', charSpacing: 1,
      })
      s.addText(c.value, {
        x: x + 0.2, y: cardY + 0.7, w: cardW - 0.4, h: 1.0,
        fontSize: 32, bold: true, color: BRAND.text, fontFace: 'Calibri',
      })
    })

    s.addText('Snapshot of portfolio scale and health at briefing time.', {
      x: 0.5, y: 4.2, w: 12.3, h: 0.4,
      fontSize: 13, italic: true, color: BRAND.muted, fontFace: 'Calibri', align: 'center',
    })
  }

  // ── Slide 3: Project Status + Health Metrics ───────────────────────────────
  {
    const s = pres.addSlide()
    addHeader(s, 'Project Status & Health')

    s.addText('Project Status', {
      x: 0.5, y: 0.9, w: 6, h: 0.4,
      fontSize: 16, bold: true, color: BRAND.text, fontFace: 'Calibri',
    })

    if (projectStatus.length > 0) {
      const chartData = [{
        name: 'Project Status',
        labels: projectStatus.map((p) => p.name),
        values: projectStatus.map((p) => p.value),
      }]
      const colors = projectStatus.map((p) => (p.color || BRAND.primaryLight).replace('#', ''))
      s.addChart(pres.ChartType.doughnut, chartData, {
        x: 0.5, y: 1.4, w: 6, h: 5.2,
        chartColors: colors,
        showLegend: true,
        legendPos: 'b',
        legendFontSize: 11,
        dataLabelFontSize: 11,
        showValue: true,
      })
    } else {
      s.addText('No project status data.', {
        x: 0.5, y: 2.0, w: 6, h: 0.4,
        fontSize: 12, italic: true, color: BRAND.muted, fontFace: 'Calibri',
      })
    }

    s.addText('Health Metrics', {
      x: 6.9, y: 0.9, w: 6, h: 0.4,
      fontSize: 16, bold: true, color: BRAND.text, fontFace: 'Calibri',
    })

    const healthRows: [string, string | number][] = healthMetrics
      ? [
          ['Portfolio Health Score', `${healthMetrics.portfolio_health_score}`],
          ['Delivery Confidence', `${healthMetrics.delivery_confidence}`],
          ['Cost at Risk', formatMoney(healthMetrics.cost_at_risk)],
          ['Schedule Stability', `${healthMetrics.schedule_stability}`],
          ['Decision Latency', `${healthMetrics.decision_latency_days} days`],
          ['Resource Stress', `${healthMetrics.resource_stress}`],
          ['Alignment Score', `${healthMetrics.alignment_score}`],
        ]
      : [['Health metrics', 'unavailable']]

    const tableRows: PptxGenJS.TableRow[] = [
      [
        { text: 'Metric', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 11 } },
        { text: 'Value', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 11 } },
      ],
      ...healthRows.map(([k, v]) => [
        { text: k, options: { fontSize: 11, color: BRAND.text } },
        { text: String(v), options: { fontSize: 11, color: BRAND.text, bold: true } },
      ]),
    ]

    s.addTable(tableRows, {
      x: 6.9, y: 1.4, w: 6, colW: [3.5, 2.5],
      border: { type: 'solid', pt: 0.5, color: 'D9E2EC' },
      fontFace: 'Calibri',
    })
  }

  // ── Slide 4: Top Risks ─────────────────────────────────────────────────────
  {
    const s = pres.addSlide()
    addHeader(s, 'Top Risks')

    const headerRow: PptxGenJS.TableRow = [
      { text: 'Risk', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 12 } },
      { text: 'Project', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 12 } },
      { text: 'Impact', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 12 } },
      { text: 'Probability', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 12 } },
      { text: 'Financial Impact', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 12 } },
    ]

    const bodyRows: PptxGenJS.TableRow[] = topRisks.length
      ? topRisks.map((r) => {
          const impact = String(r.impact ?? '')
          const impactColor = impact === 'Critical' ? BRAND.bad : impact === 'High' ? 'EA580C' : BRAND.warn
          return [
            { text: String(r.title ?? ''), options: { fontSize: 11, color: BRAND.text } },
            { text: String(r.project ?? ''), options: { fontSize: 11, color: BRAND.muted } },
            { text: impact, options: { fontSize: 11, bold: true, color: impactColor } },
            { text: `${r.probability ?? ''}%`, options: { fontSize: 11, color: BRAND.text } },
            { text: String(r.financialImpact ?? ''), options: { fontSize: 11, color: BRAND.text, bold: true } },
          ]
        })
      : [[{ text: 'No risks logged.', options: { fontSize: 11, italic: true, color: BRAND.muted, colspan: 5 } }]]

    s.addTable([headerRow, ...bodyRows], {
      x: 0.5, y: 1.0, w: 12.3,
      colW: [3.0, 2.5, 1.8, 2.0, 3.0],
      border: { type: 'solid', pt: 0.5, color: 'D9E2EC' },
      fontFace: 'Calibri',
      rowH: 0.4,
    })
  }

  // ── Slide 5: Critical Projects + Pending Approvals ─────────────────────────
  {
    const s = pres.addSlide()
    addHeader(s, 'Critical Projects & Approvals')

    s.addText('Critical Projects', {
      x: 0.5, y: 0.9, w: 6, h: 0.4,
      fontSize: 16, bold: true, color: BRAND.text, fontFace: 'Calibri',
    })

    const critHeader: PptxGenJS.TableRow = [
      { text: 'Project', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 11 } },
      { text: 'Issue', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 11 } },
      { text: 'At Risk', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 11 } },
    ]
    const critBody: PptxGenJS.TableRow[] = criticalProjects.length
      ? criticalProjects.map((c) => [
          { text: String(c.name ?? ''), options: { fontSize: 10, bold: true, color: BRAND.text } },
          { text: String(c.issue ?? ''), options: { fontSize: 10, color: BRAND.muted } },
          { text: String(c.budgetAtRisk ?? ''), options: { fontSize: 10, color: BRAND.bad, bold: true } },
        ])
      : [[{ text: 'No critical projects.', options: { fontSize: 10, italic: true, color: BRAND.muted, colspan: 3 } }]]

    s.addTable([critHeader, ...critBody], {
      x: 0.5, y: 1.4, w: 6, colW: [1.6, 3.2, 1.2],
      border: { type: 'solid', pt: 0.5, color: 'D9E2EC' },
      fontFace: 'Calibri',
      rowH: 0.4,
    })

    s.addText('Pending Approvals', {
      x: 6.9, y: 0.9, w: 6, h: 0.4,
      fontSize: 16, bold: true, color: BRAND.text, fontFace: 'Calibri',
    })

    const apprHeader: PptxGenJS.TableRow = [
      { text: 'Type', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 11 } },
      { text: 'Project / Title', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 11 } },
      { text: 'Priority', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 11 } },
      { text: 'Days', options: { bold: true, color: BRAND.white, fill: { color: BRAND.primary }, fontSize: 11 } },
    ]
    const apprBody: PptxGenJS.TableRow[] = pendingApprovals.length
      ? pendingApprovals.slice(0, 8).map((a) => {
          const prColor = a.priority === 'critical' ? BRAND.bad : a.priority === 'high' ? 'EA580C' : BRAND.warn
          return [
            { text: a.type.replace('_', ' '), options: { fontSize: 10, color: BRAND.text } },
            { text: `${a.project_name} — ${a.title}`, options: { fontSize: 10, color: BRAND.muted } },
            { text: a.priority, options: { fontSize: 10, bold: true, color: prColor } },
            { text: String(a.days_waiting), options: { fontSize: 10, color: BRAND.text } },
          ]
        })
      : [[{ text: 'No pending approvals.', options: { fontSize: 10, italic: true, color: BRAND.muted, colspan: 4 } }]]

    s.addTable([apprHeader, ...apprBody], {
      x: 6.9, y: 1.4, w: 6, colW: [1.4, 3.0, 1.0, 0.6],
      border: { type: 'solid', pt: 0.5, color: 'D9E2EC' },
      fontFace: 'Calibri',
      rowH: 0.4,
    })
  }

  // ── Slide 6: AI Insights ───────────────────────────────────────────────────
  {
    const s = pres.addSlide()
    addHeader(s, 'AI Portfolio Insights')

    const quadrants: { title: string; items: ExecutiveInsightItem[]; color: string }[] = [
      { title: "What's Working", items: normalizeInsights(aiInsights.working), color: BRAND.good },
      { title: "What's Not Working", items: normalizeInsights(aiInsights.not_working), color: BRAND.bad },
      { title: 'Needs Decision', items: normalizeInsights(aiInsights.needs_decision), color: BRAND.primaryLight },
      { title: 'Emerging Concerns', items: normalizeInsights(aiInsights.emerging_concerns), color: BRAND.warn },
    ]

    const colW = 6.1
    const rowH = 2.8
    const startX = 0.5
    const startY = 0.95
    const gap = 0.2

    quadrants.forEach((q, idx) => {
      const col = idx % 2
      const row = Math.floor(idx / 2)
      const x = startX + col * (colW + gap)
      const y = startY + row * (rowH + gap)

      s.addShape(pres.ShapeType.roundRect, {
        x, y, w: colW, h: rowH,
        fill: { color: BRAND.bg }, line: { color: 'BCCCDC', width: 1 },
        rectRadius: 0.08,
      })
      s.addShape(pres.ShapeType.rect, {
        x, y, w: 0.1, h: rowH, fill: { color: q.color }, line: { color: q.color },
      })
      s.addText(q.title, {
        x: x + 0.25, y: y + 0.1, w: colW - 0.4, h: 0.4,
        fontSize: 14, bold: true, color: BRAND.text, fontFace: 'Calibri',
      })

      if (q.items.length === 0) {
        s.addText('No insights.', {
          x: x + 0.25, y: y + 0.6, w: colW - 0.4, h: 0.4,
          fontSize: 11, italic: true, color: BRAND.muted, fontFace: 'Calibri',
        })
      } else {
        const bulletText = q.items.slice(0, 3).map((it) => ({
          text: it.summary + (it.urgency ? `  [${it.urgency.toUpperCase()}]` : ''),
          options: {
            fontSize: 11,
            color: BRAND.text,
            bullet: { type: 'bullet' as const },
            paraSpaceAfter: 4,
          },
        }))
        s.addText(bulletText, {
          x: x + 0.3, y: y + 0.55, w: colW - 0.5, h: rowH - 0.7,
          fontFace: 'Calibri', valign: 'top',
        })
      }
    })
  }

  // ── Slide 7: Closing ───────────────────────────────────────────────────────
  {
    const s = pres.addSlide()
    s.background = { color: BRAND.primary }
    s.addText('Discussion & Next Steps', {
      x: 0.6, y: 3.0, w: 12, h: 0.9,
      fontSize: 36, bold: true, color: BRAND.white, fontFace: 'Calibri', align: 'center',
    })
    s.addText('Generated from live portfolio data', {
      x: 0.6, y: 4.0, w: 12, h: 0.5,
      fontSize: 16, color: 'BCCCDC', fontFace: 'Calibri', align: 'center',
    })
  }

  await pres.writeFile({ fileName: `executive-briefing_${timestamp()}.pptx` })
}

// ─── HTML report (kept for "Generate Report") ─────────────────────────────────

function renderInsightList(items: ExecutiveInsightItem[] | string[] | undefined): string {
  if (!items || items.length === 0) return '<p class="muted">No items.</p>'
  const lis = normalizeInsights(items)
    .map((item) => {
      const urgency = item.urgency
        ? `<span class="urgency urgency-${escapeHtml(item.urgency)}">${escapeHtml(item.urgency)}</span>`
        : ''
      const reasoning = item.reasoning
        ? `<div class="reasoning">${escapeHtml(item.reasoning)}</div>`
        : ''
      return `<li><div class="summary">${escapeHtml(item.summary)} ${urgency}</div>${reasoning}</li>`
    })
    .join('')
  return `<ul class="insight-list">${lis}</ul>`
}

export function generateDashboardReport(snapshot: DashboardSnapshot) {
  const { stats, projectStatus, aiInsights, pendingApprovals, healthMetrics, topRisks, criticalProjects } = snapshot
  const generated = new Date().toLocaleString()

  const healthRows = healthMetrics
    ? Object.entries({
        'Portfolio Health Score': healthMetrics.portfolio_health_score,
        'Delivery Confidence': healthMetrics.delivery_confidence,
        'Cost at Risk': formatMoney(healthMetrics.cost_at_risk),
        'Schedule Stability': healthMetrics.schedule_stability,
        'Decision Latency (days)': healthMetrics.decision_latency_days,
        'Resource Stress': healthMetrics.resource_stress,
        'Alignment Score': healthMetrics.alignment_score,
      })
        .map(([k, v]) => `<tr><td>${escapeHtml(k)}</td><td>${escapeHtml(v)}</td></tr>`)
        .join('')
    : '<tr><td colspan="2" class="muted">No health metrics available.</td></tr>'

  const statusRows = projectStatus
    .map((s) => `<tr><td>${escapeHtml(s.name)}</td><td>${escapeHtml(s.value)}</td></tr>`)
    .join('')

  const approvalsRows = pendingApprovals.length
    ? pendingApprovals
        .map(
          (a) => `
        <tr>
          <td>${escapeHtml(a.type)}</td>
          <td>${escapeHtml(a.project_name)}</td>
          <td>${escapeHtml(a.title)}</td>
          <td>${escapeHtml(a.priority)}</td>
          <td>${escapeHtml(a.days_waiting)}</td>
        </tr>`,
        )
        .join('')
    : '<tr><td colspan="5" class="muted">No pending approvals.</td></tr>'

  const risksRows = topRisks.length
    ? topRisks
        .map(
          (r) => `
        <tr>
          <td>${escapeHtml(r.title)}</td>
          <td>${escapeHtml(r.project)}</td>
          <td>${escapeHtml(r.impact)}</td>
          <td>${escapeHtml(r.probability)}%</td>
          <td>${escapeHtml(r.financialImpact)}</td>
        </tr>`,
        )
        .join('')
    : '<tr><td colspan="5" class="muted">No risks.</td></tr>'

  const criticalRows = criticalProjects.length
    ? criticalProjects
        .map(
          (c) => `
        <tr>
          <td>${escapeHtml(c.name)}</td>
          <td>${escapeHtml(c.issue)}</td>
          <td>${escapeHtml(c.budgetAtRisk)}</td>
        </tr>`,
        )
        .join('')
    : '<tr><td colspan="3" class="muted">No critical projects.</td></tr>'

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Executive Command Center Report — ${escapeHtml(generated)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #102a43; margin: 0; padding: 32px; background: #fff; }
  h1 { margin: 0 0 4px; font-size: 28px; color: #102a43; }
  h2 { margin: 32px 0 12px; font-size: 18px; color: #102a43; border-bottom: 2px solid #486581; padding-bottom: 4px; }
  .meta { color: #486581; font-size: 13px; margin-bottom: 24px; }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 16px; }
  .card { border: 1px solid #bcccdc; border-radius: 8px; padding: 12px; background: #f0f4f8; }
  .card .label { font-size: 11px; text-transform: uppercase; color: #486581; letter-spacing: 0.5px; }
  .card .value { font-size: 22px; font-weight: 600; margin-top: 4px; color: #102a43; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 13px; }
  th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #d9e2ec; vertical-align: top; }
  th { background: #f0f4f8; font-weight: 600; color: #334e68; }
  .muted { color: #829ab1; font-style: italic; }
  .insight-list { list-style: none; padding: 0; margin: 0; }
  .insight-list li { padding: 8px 0; border-bottom: 1px solid #f0f4f8; }
  .insight-list .summary { font-weight: 500; }
  .insight-list .reasoning { font-size: 12px; color: #486581; margin-top: 2px; }
  .urgency { display: inline-block; padding: 1px 6px; border-radius: 4px; font-size: 10px; text-transform: uppercase; margin-left: 6px; }
  .urgency-critical { background: #fee2e2; color: #991b1b; }
  .urgency-high { background: #ffedd5; color: #9a3412; }
  .urgency-medium { background: #fef3c7; color: #92400e; }
  .urgency-low { background: #e5e7eb; color: #374151; }
  .insights-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
  .actions { position: fixed; top: 16px; right: 16px; display: flex; gap: 8px; }
  .actions button { padding: 8px 16px; background: #486581; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; }
  @media print {
    .actions { display: none; }
    body { padding: 16px; }
    h2 { page-break-after: avoid; }
    table, .insights-grid { page-break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="actions">
    <button onclick="window.print()">Print / Save as PDF</button>
    <button onclick="window.close()">Close</button>
  </div>

  <h1>Executive Command Center Report</h1>
  <div class="meta">Generated ${escapeHtml(generated)}</div>

  <h2>Key Metrics</h2>
  <div class="grid">
    <div class="card"><div class="label">Active Projects</div><div class="value">${escapeHtml(stats.totalProjects)}</div></div>
    <div class="card"><div class="label">Total Budget</div><div class="value">${escapeHtml(formatMoney(stats.totalBudget))}</div></div>
    <div class="card"><div class="label">At-Risk Projects</div><div class="value">${escapeHtml(stats.atRiskProjects)}</div></div>
    <div class="card"><div class="label">Portfolio Health</div><div class="value">${escapeHtml(stats.portfolioHealth)}%</div></div>
  </div>

  <h2>Project Status</h2>
  <table>
    <thead><tr><th>Status</th><th>Count</th></tr></thead>
    <tbody>${statusRows}</tbody>
  </table>

  <h2>Health Metrics</h2>
  <table>
    <thead><tr><th>Metric</th><th>Value</th></tr></thead>
    <tbody>${healthRows}</tbody>
  </table>

  <h2>AI Portfolio Insights</h2>
  <div class="insights-grid">
    <div>
      <h3>What's Working</h3>
      ${renderInsightList(aiInsights.working)}
    </div>
    <div>
      <h3>What's Not Working</h3>
      ${renderInsightList(aiInsights.not_working)}
    </div>
    <div>
      <h3>Needs Decision</h3>
      ${renderInsightList(aiInsights.needs_decision)}
    </div>
    <div>
      <h3>Emerging Concerns</h3>
      ${renderInsightList(aiInsights.emerging_concerns)}
    </div>
  </div>

  <h2>Pending Approvals</h2>
  <table>
    <thead><tr><th>Type</th><th>Project</th><th>Title</th><th>Priority</th><th>Days Waiting</th></tr></thead>
    <tbody>${approvalsRows}</tbody>
  </table>

  <h2>Top Risks</h2>
  <table>
    <thead><tr><th>Title</th><th>Project</th><th>Impact</th><th>Probability</th><th>Financial Impact</th></tr></thead>
    <tbody>${risksRows}</tbody>
  </table>

  <h2>Critical Projects</h2>
  <table>
    <thead><tr><th>Project</th><th>Issue</th><th>Budget at Risk</th></tr></thead>
    <tbody>${criticalRows}</tbody>
  </table>
</body>
</html>`

  const reportWindow = window.open('', '_blank', 'width=1100,height=800')
  if (!reportWindow) {
    throw new Error('Popup blocked — allow popups to generate the report.')
  }
  reportWindow.document.open()
  reportWindow.document.write(html)
  reportWindow.document.close()
}

// Note: also re-import the type so the namespace exists at type position
import type PptxGenJS from 'pptxgenjs'
